import { type AxiosResponse } from "axios";

import { type FlowFiltersEnum, flowFilterTypes } from "../../../../Library/Flows/types/models";
import { AddPeopleToContentTypes, AssignmentPeopleContext } from "../../../../../enums";
import { formatFilters } from "../../../../../utils/filterMapUtils";
import { type FilterTypesMap, type GenericFiltersMap } from "../../../../../utils/filterUtils";
import { escapeTerm } from "../../../../../utils/searchUtils";
import { formatSortingV2api } from "../../../../../utils/sortingUtils";
import { type AppDispatch, type AppThunk, type RootState } from "../../../../Application/globaltypes/redux";
import DataService from "../../../../Application/services/dataServices/typedDataService";

import { contentContextSelector } from "../../../state/slices/addToContentSlice";
import { type PeopleAvailableFlow } from "../../models";
import { fetchBegin, fetchFailure, fetchSuccess } from "../slices/contentAssignmentsSlice";

const takeCount = Number(process.env.REACT_APP_LOAD_ITEMS_COUNT) || 30;
const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;

export interface AvailableItemsPayload<TFilters extends string | number> {
  userIds: number[];
  search?: string;
  skip: number;
  orderBy: string;
  filterParams: GenericFiltersMap<TFilters>;
}

export const fetchPeopleAvailableItems =
  <TFilters extends string | number>(
    payload: AvailableItemsPayload<TFilters>,
    context: AssignmentPeopleContext,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    await dispatch(fetchPeopleAvailableFlows(payload as AvailableItemsPayload<FlowFiltersEnum>, context));
  };

const fetchPeopleAvailableFlows =
  (payload: AvailableItemsPayload<FlowFiltersEnum>, context: AssignmentPeopleContext): AppThunk =>
  async (dispatch: AppDispatch) => {
    const { userIds } = payload;

    const request = (() => {
      const map = {
        [AssignmentPeopleContext.Group]: DataService.groups.getGroupAvailableFlowsV4,
        [AssignmentPeopleContext.User]: DataService.users.getUserAvailableFlowsV4,
        [AssignmentPeopleContext.TemplateGroup]: DataService.groups.getGroupAvailableFlowsV4,
      };
      return () => map[context](userIds, setConfig(payload, flowFilterTypes));
    })();

    await dispatch(doFetch(request, AddPeopleToContentTypes.Flows));
  };

const doFetch =
  (request: () => Promise<AxiosResponse<PeopleAvailableFlow[]>>, context: AddPeopleToContentTypes): AppThunk =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(fetchBegin());

    try {
      const result = await request();

      // data should not be re-writed by outdated requests
      if (contentContextSelector(getState()) !== context) {
        return;
      }

      dispatch(fetchSuccess({ items: result.data, totalCount: Number.parseInt(result.headers[countHeaderName]) }));
    } catch (error: any) {
      const err = error as Error;
      dispatch(fetchFailure({ message: err.message, name: err.name }));
    }
  };

const setConfig = <TFilters extends string | number>(
  payload: AvailableItemsPayload<TFilters>,
  filterTypes: FilterTypesMap<TFilters>,
) => {
  const { filterParams, orderBy, skip, search } = payload;

  const { sortBy, sortOrder } = formatSortingV2api(orderBy);
  return {
    skip,
    top: takeCount,
    ...formatFilters(filterParams, filterTypes),
    term: escapeTerm(search),
    sortBy,
    sortOrder,
    showPurchased: true,
  };
};
